import { useAtomValue } from 'jotai';
import { ReactElement } from 'react';

import {
  ClientActivityContentLocation,
  ClientInformationDrawerBodyContentConnected as ClientInformationDrawerBodyContent,
  useRegisterDrawerRef,
} from '@grow-therapy-team/seedling-components';

import {
  isInSessionWithSelectedClientAtom,
  providerShortIdAtom,
  selectedClientInfoAtom,
  selectedClientUserShortIdAtom,
} from '../state';
import { ClientInformationTabWrapper as ClientInformationTab } from './ClientInformationTab';
import { UpcomingClientAppointmentsTabWrapper as UpcomingClientAppointmentsTab } from './client-appointments-tab/UpcomingClientAppointmentsTab';
import { TreatmentTabWrapper as TreatmentTab } from './treatment-tab/TreatmentTab';
import { DrawerRefKey } from './utils/types';

/**
 * Wrapper component for the client activity tab in the Seedlings drawer. This
 * component manages ref registration for programmatic focus/scroll from
 * pre-visit summary resource buttons.
 */
function ClientActivityTabWrapper({ children }: { children: ReactElement }) {
  const clientActivityRef = useRegisterDrawerRef<DrawerRefKey, HTMLDivElement>(DrawerRefKey.CLIENT_ACTIVITY_CARD);

  return (
    <div
      ref={clientActivityRef}
      // tabIndex={-1} allows the div to be programmatically focused when
      // navigating from pre-visit summary resource buttons, but excludes it
      // from keyboard tab order
      tabIndex={-1}
      aria-label='Client activity between sessions'
    >
      {children}
    </div>
  );
}

export function ClientInformationDrawerBodyContentWrapper({ isEmpty }: { isEmpty: boolean }) {
  const patientShortId = useAtomValue(selectedClientInfoAtom)?.patientShortId ?? '';
  const isInSession = useAtomValue(isInSessionWithSelectedClientAtom);
  const providerShortId = useAtomValue(providerShortIdAtom);
  const coachTabLocation = isInSession
    ? ClientActivityContentLocation.InSessionTelehealth
    : ClientActivityContentLocation.OutOfSessionTelehealth;
  const clientUserShortId = useAtomValue(selectedClientUserShortIdAtom) ?? '';

  return (
    <ClientInformationDrawerBodyContent
      location={coachTabLocation}
      patientShortId={patientShortId}
      clientUserShortId={clientUserShortId}
      clientInformationTabContent={<ClientInformationTab isEmpty={isEmpty} />}
      treatmentTabContent={<TreatmentTab />}
      appointmentTabContent={<UpcomingClientAppointmentsTab />}
      providerShortId={providerShortId ?? ''}
      clientActivityTabWrapperComponent={ClientActivityTabWrapper}
    />
  );
}
